import { CHART_SUPPORT_THRESHOLD } from 'common/constants/chart'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Box, Typography } from '@mui/material'

/** Format CHART_SUPPORT_THRESHOLD (ISO string) as a short Thai date, e.g. "10 เม.ย. 2569" */
function formatThresholdDate(iso: string): string {
  try {
    // Buddhist Era offset: +543 years
    const date = new Date(iso)
    const day = format(date, 'd', { locale: th })
    const month = format(date, 'MMM', { locale: th })
    const year = date.getFullYear() + 543
    return `${day} ${month} ${year}`
  } catch {
    return iso
  }
}

const DEFAULT_THRESHOLD_LABEL = formatThresholdDate(CHART_SUPPORT_THRESHOLD)

interface ChartFallbackProps {
  /** Height of the fallback container (default: 320) */
  height?: number
  /** Main title text */
  title?: string
  /** Secondary description text */
  description?: string
  /** Path to the illustration image */
  img?: string
}

/**
 * Fallback UI displayed when chart data is unavailable
 * (e.g. data only exists from a certain date onward).
 * The threshold date is derived from CHART_SUPPORT_THRESHOLD automatically.
 */
export default function ChartFallback({
  height = 320,
  title = 'ไม่สามารถแสดงผลได้',
  description = `รองรับเฉพาะรอบการเลี้ยงที่เริ่มตั้งแต่\n${DEFAULT_THRESHOLD_LABEL}`,
  img = '/static/chart/chart_error.svg',
}: ChartFallbackProps) {
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: (theme) => theme.spacing(1.5),
        py: (theme) => theme.spacing(4),
      }}
    >
      <Box
        component="img"
        src={img}
        alt="chart unavailable"
        sx={{ width: 184, height: 117, objectFit: 'contain' }}
      />

      <Typography
        variant="body1"
        fontWeight={600}
        textAlign="center"
        sx={{ color: 'text.primary' }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
        >
          {description}
        </Typography>
      )}
    </Box>
  )
}
